import React from 'react';
import StatsWrapper from '../../utils/StatsWrapper';
import { recordPendingDealClick } from '../../utils/dealFollowupStorage';

/**
 * Single source of truth for a clickable outbound Amazon product link.
 * Renders a real <a> (not a button + window.open) so native browser
 * behaviors keep working - middle-click, right-click "copy link", etc.
 * Every render of this component tracks its click the same way, so a new
 * call site can't forget to instrument it.
 *
 * Found two real gaps before this existed: ProductCard's cart-icon button
 * and TrackedItemCard's shop button/image both opened Amazon with zero
 * tracking - never instrumented even under the old Mixpanel setup. For call
 * sites that can't render a real <a> (a library Button component, an
 * onClick on an <img>), use openAndTrackAmazonLink from
 * ../../utils/amazonLinkTracking.js instead - same tracking call, same
 * guarantee, just without the anchor semantics.
 *
 * @param {Object} props
 * @param {string} props.href - Amazon product page URL
 * @param {string} props.eventName - stats event name to track on click
 * @param {Object} [props.eventProperties] - opt-in event properties
 * @param {string} [props.dealFollowupItemName] - display name for the post-click-out
 *   follow-up popup (docs/deal-signal-design.md §6). Optional - the popup falls back to
 *   a generic phrasing when omitted rather than requiring every call site to thread it.
 * @param {string} [props.className]
 * @param {Function} [props.onClick] - additional click handler, called after tracking
 * @param {React.ReactNode} props.children
 */
function AmazonLink({ href, eventName, eventProperties, dealFollowupItemName, className, onClick, children, ...rest }) {
  const handleClick = (e) => {
    StatsWrapper.track(eventName, eventProperties);
    if (eventProperties?.asin) {
      recordPendingDealClick(eventProperties.asin, dealFollowupItemName);
    }
    onClick?.(e);
  };

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      onClick={handleClick}
      {...rest}
    >
      {children}
    </a>
  );
}

export default AmazonLink;
